import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, X, ChevronDown, ChevronUp, Pill } from 'lucide-react';
import MedicationAutocomplete from './MedicationAutocomplete';
import DosageAutocomplete from './DosageAutocomplete';
import InstructionsAutocomplete from './InstructionsAutocomplete';

// Vias de administração disponíveis
const VIAS_ADMINISTRACAO = [
  { value: 'ORAL', label: 'USO ORAL', color: 'bg-emerald-500' },
  { value: 'TÓPICO', label: 'USO TÓPICO', color: 'bg-purple-500' },
  { value: 'SUBCUTÂNEO', label: 'USO SUBCUTÂNEO', color: 'bg-blue-500' },
  { value: 'INTRAMUSCULAR', label: 'USO INTRAMUSCULAR', color: 'bg-orange-500' },
  { value: 'INJETÁVEL', label: 'USO INJETÁVEL', color: 'bg-red-500' },
  { value: 'OFTALMOLÓGICO', label: 'USO OFTALMOLÓGICO', color: 'bg-cyan-500' },
  { value: 'INALATÓRIO', label: 'USO INALATÓRIO', color: 'bg-indigo-500' },
  { value: 'OTOLÓGICO', label: 'USO OTOLÓGICO', color: 'bg-pink-500' },
  { value: 'NASAL', label: 'USO NASAL', color: 'bg-teal-500' },
  { value: 'RETAL', label: 'USO RETAL', color: 'bg-amber-500' },
  { value: 'VAGINAL', label: 'USO VAGINAL', color: 'bg-rose-500' },
  { value: 'SUBLINGUAL', label: 'USO SUBLINGUAL', color: 'bg-lime-500' },
];

// Tipos de embalagem
const PACKAGING_TYPES = [
  { value: 'comprimidos', label: 'Comprimido(s)' },
  { value: 'cartelas', label: 'Cartela(s)' },
  { value: 'frascos', label: 'Frasco(s)' },
  { value: 'ampolas', label: 'Ampola(s)' },
  { value: 'tubos', label: 'Tubo(s)' },
  { value: 'caixas', label: 'Caixa(s)' },
  { value: 'envelopes', label: 'Envelope(s)' },
  { value: 'saches', label: 'Sachê(s)' },
  { value: 'unidades', label: 'Unidade(s)' },
  { value: 'uso_continuo', label: 'Uso Contínuo' },
];

// Unidades de dosagem
const DOSAGE_UNITS = [
  { value: 'MG', label: 'MG' },
  { value: 'MCG', label: 'MCG' },
  { value: 'MCG/JATO', label: 'MCG/JATO' },
  { value: 'G', label: 'G' },
  { value: 'ML', label: 'ML' },
  { value: 'MG/ML', label: 'MG/ML' },
  { value: 'G/ML', label: 'G/ML' },
  { value: 'MCG/ML', label: 'MCG/ML' },
  { value: 'MG/G', label: 'MG/G' },
  { value: 'UI/ML', label: 'UI/ML' },
  { value: 'UI', label: 'UI' },
  { value: '%', label: '%' },
];

// Medicamento vazio padrão
const createEmptyMedication = () => ({
  name: '',
  dosage: '',
  dosage_unit: 'MG',
  packaging_type: 'comprimidos',
  quantity: '',
  instructions: ''
});

// Componente de um único medicamento
function MedicationItem({ medication, index, onUpdate, onRemove, canRemove }) {
  return (
    <div className="bg-white rounded-lg p-4 border border-gray-200 relative shadow-sm">
      {canRemove && (
        <button
          type="button"
          onClick={onRemove}
          className="absolute top-2 right-2 text-red-400 hover:text-red-600 transition-colors"
          data-testid={`remove-medication-${index}`}
        >
          <X className="w-4 h-4" />
        </button>
      )}
      
      {/* Nome e Dosagem */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="md:col-span-2">
          <Label className="text-xs font-medium text-gray-600 mb-1 block">Nome do Medicamento *</Label>
          <MedicationAutocomplete
            value={medication.name}
            onChange={(value) => onUpdate('name', value)}
            placeholder="Ex: LOSARTANA"
            className="h-10"
            dataTestId={`medication-name-${index}`}
          />
        </div>
        <div>
          <Label className="text-xs font-medium text-gray-600 mb-1 block">Dosagem</Label>
          <div className="flex gap-2">
            <DosageAutocomplete
              value={medication.dosage}
              onChange={(value) => {
                const match = value.match(/^(\d+(?:[.,]\d+)?)\s*([A-Z%/]+)?$/i);
                if (match) {
                  onUpdate('dosage', match[1].replace(',', '.'));
                  if (match[2]) {
                    onUpdate('dosage_unit', match[2].toUpperCase());
                  }
                } else {
                  onUpdate('dosage', value);
                }
              }}
              placeholder="50"
              className="h-10 flex-1"
              dataTestId={`medication-dosage-${index}`}
              medicationName={medication.name}
            />
            <Select 
              value={medication.dosage_unit || 'MG'} 
              onValueChange={(value) => onUpdate('dosage_unit', value)}
            >
              <SelectTrigger className="h-10 w-24">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DOSAGE_UNITS.map(unit => (
                  <SelectItem key={unit.value} value={unit.value}>{unit.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>
      
      {/* Tipo de Embalagem e Quantidade */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
        <div>
          <Label className="text-xs font-medium text-gray-600 mb-1 block">Tipo de Embalagem</Label>
          <Select 
            value={medication.packaging_type || 'comprimidos'} 
            onValueChange={(value) => onUpdate('packaging_type', value)}
          >
            <SelectTrigger className="h-10" data-testid={`packaging-type-${index}`}>
              <SelectValue placeholder="Selecione" />
            </SelectTrigger>
            <SelectContent>
              {PACKAGING_TYPES.map(type => (
                <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {medication.packaging_type !== 'uso_continuo' && (
          <div>
            <Label className="text-xs font-medium text-gray-600 mb-1 block">
              Quantidade
            </Label>
            <Input
              data-testid={`medication-quantity-${index}`}
              value={medication.quantity}
              onChange={(e) => onUpdate('quantity', e.target.value)}
              placeholder="30"
              className="h-10"
            />
          </div>
        )}
      </div>
      
      {/* Instruções */}
      <div className="mt-3">
        <Label className="text-xs font-medium text-gray-600 mb-1 block">Posologia / Instruções de Uso</Label>
        <InstructionsAutocomplete
          value={medication.instructions}
          onChange={(value) => onUpdate('instructions', value)}
          placeholder="TOMAR 01 COMPRIMIDO DE 12 EM 12 HORAS"
          className="h-10"
          dataTestId={`medication-instructions-${index}`}
          medicationName={medication.name}
        />
      </div>
    </div>
  );
}

// Componente de um grupo de medicamentos por via
function MedicationRouteGroup({ 
  group, 
  groupIndex, 
  onUpdateGroup, 
  onRemoveGroup, 
  canRemove,
  collapsed,
  onToggleCollapse
}) {
  const viaInfo = VIAS_ADMINISTRACAO.find(v => v.value === group.via) || VIAS_ADMINISTRACAO[0];
  const medicationCount = group.medicamentos.length;
  
  const addMedication = () => {
    const updatedMedicamentos = [...group.medicamentos, createEmptyMedication()];
    onUpdateGroup({ ...group, medicamentos: updatedMedicamentos });
  };
  
  const removeMedication = (medIndex) => {
    if (group.medicamentos.length > 1) {
      const updatedMedicamentos = group.medicamentos.filter((_, i) => i !== medIndex);
      onUpdateGroup({ ...group, medicamentos: updatedMedicamentos });
    }
  };
  
  const updateMedication = (medIndex, field, value) => {
    const updatedMedicamentos = [...group.medicamentos];
    updatedMedicamentos[medIndex] = { ...updatedMedicamentos[medIndex], [field]: value };
    onUpdateGroup({ ...group, medicamentos: updatedMedicamentos });
  };
  
  const updateVia = (newVia) => {
    onUpdateGroup({ ...group, via: newVia });
  };
  
  return (
    <div className="border-2 border-gray-200 rounded-xl overflow-hidden bg-gray-50">
      {/* Header do grupo */}
      <div 
        className={`${viaInfo.color} bg-opacity-10 p-4 flex items-center justify-between cursor-pointer hover:bg-opacity-20 transition-all`}
        onClick={onToggleCollapse}
      >
        <div className="flex items-center gap-3">
          <div className={`w-3 h-3 rounded-full ${viaInfo.color}`}></div>
          <div>
            <h4 className="font-semibold text-gray-800">{viaInfo.label}</h4>
            <p className="text-xs text-gray-500">{medicationCount} medicamento(s)</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {canRemove && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                onRemoveGroup();
              }}
              className="text-red-500 hover:text-red-700 hover:bg-red-50"
              data-testid={`remove-group-${groupIndex}`}
            >
              <X className="w-4 h-4" />
            </Button>
          )}
          {collapsed ? (
            <ChevronDown className="w-5 h-5 text-gray-500" />
          ) : (
            <ChevronUp className="w-5 h-5 text-gray-500" />
          )}
        </div>
      </div>
      
      {/* Conteúdo do grupo (colapsável) */}
      {!collapsed && (
        <div className="p-4 space-y-4">
          {/* Seletor de via */}
          <div>
            <Label className="text-xs font-medium text-gray-600 mb-2 block">Via de Administração</Label>
            <div className="flex flex-wrap gap-2">
              {VIAS_ADMINISTRACAO.map((via) => (
                <Button
                  key={via.value}
                  type="button"
                  onClick={() => updateVia(via.value)}
                  size="sm"
                  className={`rounded-full transition-all ${
                    group.via === via.value
                      ? `${via.color} text-white shadow-md`
                      : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-100'
                  }`}
                  data-testid={`group-${groupIndex}-via-${via.value.toLowerCase()}`}
                >
                  {via.value}
                </Button>
              ))}
            </div>
          </div>
          
          {/* Lista de medicamentos */}
          <div className="space-y-3">
            {group.medicamentos.map((med, medIndex) => (
              <MedicationItem
                key={medIndex}
                medication={med}
                index={medIndex}
                onUpdate={(field, value) => updateMedication(medIndex, field, value)}
                onRemove={() => removeMedication(medIndex)}
                canRemove={group.medicamentos.length > 1}
              />
            ))}
          </div>
          
          {/* Botão adicionar medicamento */}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={addMedication}
            className="w-full rounded-full border-dashed border-2 hover:border-solid"
            data-testid={`group-${groupIndex}-add-medication`}
          >
            <Plus className="w-4 h-4 mr-2" />
            Adicionar Medicamento a este grupo
          </Button>
        </div>
      )}
    </div>
  );
}

// Componente principal exportado
export default function MedicationGroupByRoute({ 
  groups, 
  onChange,
  dataTestIdPrefix = ''
}) {
  const [collapsedGroups, setCollapsedGroups] = useState({});
  
  const toggleGroupCollapse = (index) => {
    setCollapsedGroups(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };
  
  const addGroup = () => {
    const newGroup = {
      via: 'ORAL',
      medicamentos: [createEmptyMedication()]
    };
    onChange([...groups, newGroup]);
  };
  
  const removeGroup = (index) => {
    if (groups.length > 1) {
      onChange(groups.filter((_, i) => i !== index));
    }
  };
  
  const updateGroup = (index, updatedGroup) => {
    const newGroups = [...groups];
    newGroups[index] = updatedGroup;
    onChange(newGroups);
  };
  
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Pill className="w-5 h-5 text-primary" />
          <Label className="text-lg font-bold">Medicamentos por Via de Administração</Label>
        </div>
        <Button
          type="button"
          onClick={addGroup}
          variant="outline"
          size="sm"
          className="rounded-full bg-primary/10 text-primary hover:bg-primary/20 border-primary/30"
          data-testid={`${dataTestIdPrefix}add-route-group`}
        >
          <Plus className="w-4 h-4 mr-2" />
          Adicionar Outra Via
        </Button>
      </div>
      
      <p className="text-sm text-muted-foreground">
        Organize os medicamentos por via de administração. Cada grupo pode conter múltiplos medicamentos.
      </p>
      
      <div className="space-y-4">
        {groups.map((group, index) => (
          <MedicationRouteGroup
            key={index}
            group={group}
            groupIndex={index}
            onUpdateGroup={(updatedGroup) => updateGroup(index, updatedGroup)}
            onRemoveGroup={() => removeGroup(index)}
            canRemove={groups.length > 1}
            collapsed={collapsedGroups[index]}
            onToggleCollapse={() => toggleGroupCollapse(index)}
          />
        ))}
      </div>
    </div>
  );
}

// Export das constantes para uso em outros componentes
export { VIAS_ADMINISTRACAO, PACKAGING_TYPES, DOSAGE_UNITS, createEmptyMedication };
